import { useState, useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { 
  FileText, 
  CheckCircle, 
  AlertCircle,
  Loader2,
  Trash2,
  Save,
  FileUp,
  Building2
} from 'lucide-react';
import { useExpenses } from '../contexts/ExpenseContext';
import * as pdfjsLib from 'pdfjs-dist';

// Set up PDF.js worker
pdfjsLib.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjsLib.version}/pdf.worker.min.js`;

type CompanyType = 'Bennett' | 'Auto Driveaway' | 'Coldiron' | 'Quality Driveaway' | 'Other';

interface ParsedSettlement {
  id: string;
  company: CompanyType;
  date: string;
  controlNumber: string;
  terminal: string;
  origin: string;
  destination: string;
  miles: number;
  lineHaul: number;
  fuelSurcharge: number;
  tolls: number;
  otherPay: number;
  totalPay: number;
  deductions: number;
  checkAmount: number;
  selected: boolean;
}

export default function ImportSettlement() {
  const { addExpense, expenses } = useExpenses();
  const [pdfText, setPdfText] = useState('');
  const [parsedSettlements, setParsedSettlements] = useState<ParsedSettlement[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [importStatus, setImportStatus] = useState<{ success: number; errors: number; skipped: number } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check if a settlement already exists in the database
  const isDuplicate = useCallback((controlNumber: string): boolean => {
    return expenses.some(exp => 
      exp.freightBillNumber === controlNumber || 
      exp.filename?.includes(controlNumber)
    );
  }, [expenses]);

  // Parse Auto Driveaway settlement
  const parseAutoDriveawaySettlement = (text: string): ParsedSettlement | null => {
    try {
      // Look for Auto Driveaway specific patterns
      // BOL format: 048-024596-033
      const bolMatch = text.match(/(?:BOL|breakdown of pay for BOL)\s*[\n\s]*(\d{3}-\d{6}-\d{3})/i);
      if (!bolMatch) return null;
      
      const bolNumber = bolMatch[1];
      
      // Extract date (MM/DD/YYYY format at top)
      const dateMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      let formattedDate = '';
      if (dateMatch) {
        const parts = dateMatch[1].split('/');
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      
      // Extract miles
      const milesMatch = text.match(/per mile\s*[X×x]\s*(\d+)/i);
      const miles = milesMatch ? parseInt(milesMatch[1]) : 0;
      
      // Extract total pay for this move
      const totalPayMatch = text.match(/Total pay for this move\s*\$?([\d,]+\.?\d*)/i);
      const totalPay = totalPayMatch ? parseFloat(totalPayMatch[1].replace(/,/g, '')) : 0;
      
      // Extract settlement amount
      const settlementMatch = text.match(/Total Settlement\s*\$?([\d,]+\.?\d*)/i);
      const checkAmount = settlementMatch ? parseFloat(settlementMatch[1].replace(/,/g, '')) : 0;
      
      // Extract advance
      const advanceMatch = text.match(/Total Advance\s*\$?([\d,]+\.?\d*)/i);
      const advance = advanceMatch ? parseFloat(advanceMatch[1].replace(/,/g, '')) : 0;
      
      // Extract fuel
      const fuelMatch = text.match(/Fuel (?:allowance|receipts)[^\d]*\$?([\d,]+\.?\d*)/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      
      // Extract tolls/permits
      const tollsMatch = text.match(/Tolls\s*[&]\s*Permits\s*\$?([\d,]+\.?\d*)/i);
      const tolls = tollsMatch ? parseFloat(tollsMatch[1].replace(/,/g, '')) : 0;
      
      // Extract pickup/travel
      const travelMatch = text.match(/Pickup\/Travel\s*\$?([\d,]+\.?\d*)/i);
      const travel = travelMatch ? parseFloat(travelMatch[1].replace(/,/g, '')) : 0;
      
      if (totalPay === 0) return null;
      
      return {
        id: crypto.randomUUID(),
        company: 'Auto Driveaway',
        date: formattedDate,
        controlNumber: bolNumber,
        terminal: 'AD',
        origin: '',
        destination: '',
        miles,
        lineHaul: totalPay - fuel - tolls - travel,
        fuelSurcharge: fuel,
        tolls,
        otherPay: travel,
        totalPay,
        deductions: advance,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Auto Driveaway parse error:', e);
      return null;
    }
  };

  // Parse Coldiron Specialized Driveaway settlement (Remittance Advice format)
  const parseColdiron = (text: string): ParsedSettlement | null => {
    try {
      // Look for Coldiron specific pattern
      if (!text.includes('Coldiron') && !text.includes('Driver Card')) return null;
      
      // Extract Invoice #
      const invoiceMatch = text.match(/Invoice #[:\s]*(\d+)/i) || text.match(/(\d{6})\s+\w+,\w{2}/);
      if (!invoiceMatch) return null;
      const invoiceNum = invoiceMatch[1];
      
      // Extract Paid Date
      const dateMatch = text.match(/Paid Date\s*(\d{1,2}\/\d{1,2}\/\d{4})/i);
      let formattedDate = '';
      if (dateMatch) {
        const parts = dateMatch[1].split('/');
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      
      // Extract location from Ref column
      const locationMatch = text.match(/Ref[:\s]+(\w+,\w{2})/i) || text.match(/(\w+),(\w{2}):Driver Pay/i);
      const location = locationMatch ? locationMatch[1] || `${locationMatch[1]},${locationMatch[2]}` : '';
      
      // Extract Driver Pay
      const driverPayMatch = text.match(/Driver Pay[^\d]*(\d+\.?\d*)/i);
      const driverPay = driverPayMatch ? parseFloat(driverPayMatch[1]) : 0;
      
      // Extract Fuel Surcharge
      const fuelMatch = text.match(/Fuel Surcharge[^\d]*(\d+\.?\d*)/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1]) : 0;
      
      // Extract Deadhead
      const deadheadMatch = text.match(/Deadhead[^\d]*(\d+\.?\d*)/i);
      const deadhead = deadheadMatch ? parseFloat(deadheadMatch[1]) : 0;
      
      // Extract Tolls
      const tollsMatch = text.match(/Tolls[^\d]*(\d+\.?\d*)/i);
      const tolls = tollsMatch ? parseFloat(tollsMatch[1]) : 0;
      
      // Extract Total for Payment (net)
      const totalMatch = text.match(/Total for Payment[^\d]*(\d+\.?\d*)/i);
      const checkAmount = totalMatch ? parseFloat(totalMatch[1]) : 0;
      
      // Calculate total pay (gross before deductions)
      const totalPay = driverPay + fuel + deadhead + tolls;
      
      if (totalPay === 0) return null;
      
      return {
        id: crypto.randomUUID(),
        company: 'Coldiron',
        date: formattedDate,
        controlNumber: invoiceNum,
        terminal: 'CSD',
        origin: '',
        destination: location,
        miles: 0,
        lineHaul: driverPay,
        fuelSurcharge: fuel,
        tolls,
        otherPay: deadhead,
        totalPay,
        deductions: totalPay - checkAmount,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Coldiron parse error:', e);
      return null;
    }
  };

  // Parse Quality Driveaway / Generic "Driver #" format settlements
  const parseQualityDriveaway = (section: string): ParsedSettlement | null => {
    try {
      // Look for Driver # pattern
      const driverMatch = section.match(/Driver #(\d+)/i);
      if (!driverMatch) return null;
      
      // Extract Order #
      const orderMatch = section.match(/Order #[:\s]*(\d+)/i) || section.match(/^(\d{6})/m);
      if (!orderMatch) return null;
      const orderNum = orderMatch[1];
      
      // Extract Date
      const dateMatch = section.match(/(\d{1,2}\/\d{1,2}\/\d{4})/);
      let formattedDate = '';
      if (dateMatch) {
        const parts = dateMatch[1].split('/');
        formattedDate = `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
      }
      
      // Extract route from Description
      const routeMatch = section.match(/([A-Z]+,\s*[A-Z]{2})\/?[^to]*to\s*([A-Z]+,\s*[A-Z]{2})/i);
      const origin = routeMatch ? routeMatch[1].trim() : '';
      const destination = routeMatch ? routeMatch[2].trim() : '';
      
      // Extract Miles
      const milesMatch = section.match(/Mileage Pay\s*\|\s*(\d+)/i) || section.match(/Miles[:\s]*(\d+)/i);
      const miles = milesMatch ? parseInt(milesMatch[1]) : 0;
      
      // Extract Mileage Pay
      const mileagePayMatch = section.match(/Mileage Pay[^\$]*\$?([\d,]+\.?\d*)/i);
      const mileagePay = mileagePayMatch ? parseFloat(mileagePayMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Flat Pay if no mileage pay
      const flatPayMatch = section.match(/Flat Pay Rate[^\$]*\$?([\d,]+\.?\d*)/i);
      const flatPay = flatPayMatch ? parseFloat(flatPayMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Fuel Surcharge
      const fuelMatch = section.match(/Fuel Surcharge[^\$]*\$?([\d,]+\.?\d*)/i);
      const fuel = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Total Taxable Earnings
      const totalMatch = section.match(/Total Taxable Earnings[^\$]*\$?([\d,]+\.?\d*)/i);
      const totalPay = totalMatch ? parseFloat(totalMatch[1].replace(/,/g, '')) : (mileagePay + flatPay + fuel);
      
      // Extract Total Settlement Deductions
      const deductMatch = section.match(/Total Settlement Deductions[^\$\(]*\(?\$?([\d,]+\.?\d*)/i);
      const deductions = deductMatch ? parseFloat(deductMatch[1].replace(/,/g, '')) : 0;
      
      // Extract Net Settlement
      const netMatch = section.match(/Net Settlement[^\$]*\$?([\d,]+\.?\d*)/i);
      const checkAmount = netMatch ? parseFloat(netMatch[1].replace(/,/g, '')) : (totalPay - deductions);
      
      if (totalPay === 0) return null;
      
      return {
        id: crypto.randomUUID(),
        company: 'Quality Driveaway',
        date: formattedDate,
        controlNumber: orderNum,
        terminal: 'QDA',
        origin,
        destination,
        miles,
        lineHaul: mileagePay || flatPay,
        fuelSurcharge: fuel,
        tolls: 0,
        otherPay: 0,
        totalPay,
        deductions,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Quality Driveaway parse error:', e);
      return null;
    }
  };

  // Parse Bennett settlement
  const parseBennettSettlement = (section: string): ParsedSettlement | null => {
    try {
      // Extract control number
      const controlMatch = section.match(/CONTROL #:\s*(\d+)/i);
      if (!controlMatch) return null;
      
      const controlNumber = controlMatch[1];
      
      // Extract date
      const dateMatch = section.match(/DATE:\s*(\d{1,2}\/\d{1,2}\/\d{2,4})/i);
      const dateStr = dateMatch ? dateMatch[1] : '';
      
      // Extract terminal
      const terminalMatch = section.match(/TERMINAL:\s*(\w+)/i);
      const terminal = terminalMatch ? terminalMatch[1] : '';
      
      // Extract origin and destination
      const routeMatch = section.match(/ORIG:\s*([^D]+?)DEST:\s*([^M]+?)MILES:/i) ||
                        section.match(/ORIG[:\s]+([A-Z\s,]+?)\s+DEST[:\s]+([A-Z\s,]+?)\s+MILES/i);
      const origin = routeMatch ? routeMatch[1].trim().replace(/\s+/g, ' ') : '';
      const destination = routeMatch ? routeMatch[2].trim().replace(/\s+/g, ' ') : '';
      
      // Extract miles
      const milesMatch = section.match(/(?:BILLED )?MILES:\s*([\d,]+)/i);
      const miles = milesMatch ? parseInt(milesMatch[1].replace(/,/g, '')) : 0;
      
      // Extract line haul pay
      const lineHaulMatch = section.match(/(?:FLAT RATE )?LINE ?HAUL(?: PAY)?[^$\d]*\$?([\d,]+\.?\d*)/i);
      const lineHaul = lineHaulMatch ? parseFloat(lineHaulMatch[1].replace(/,/g, '')) : 0;
      
      // Extract fuel surcharge
      const fuelMatch = section.match(/FUEL SURCHARGE[^$\d]*\$?([\d,]+\.?\d*)/i);
      const fuelSurcharge = fuelMatch ? parseFloat(fuelMatch[1].replace(/,/g, '')) : 0;
      
      // Extract tolls
      const tollsMatch = section.match(/TOLLS[^$\d]*\$?([\d,]+\.?\d*)/i);
      const tolls = tollsMatch ? parseFloat(tollsMatch[1].replace(/,/g, '')) : 0;
      
      // Extract total pay before deductions
      const totalPayMatch = section.match(/TOTAL PAY BEFORE DEDUCTIONS[:\s]*\$?([\d,]+\.?\d*)/i);
      const totalPay = totalPayMatch ? parseFloat(totalPayMatch[1].replace(/,/g, '')) : 0;
      
      // Extract total deductions
      const deductionsMatch = section.match(/TOTAL DEDUCTIONS[:\s]*\$?-?([\d,]+\.?\d*)/i);
      const deductions = deductionsMatch ? parseFloat(deductionsMatch[1].replace(/,/g, '')) : 0;
      
      // Extract check amount
      const checkMatch = section.match(/(?:CHECK AMOUNT|BALANCE TO BE PAID)[:\s]*\$?([\d,]+\.?\d*)/i);
      const checkAmount = checkMatch ? parseFloat(checkMatch[1].replace(/,/g, '')) : 0;
      
      // Skip void settlements
      if (checkAmount === 0 && section.toLowerCase().includes('void')) return null;
      
      // Calculate other pay (transportation, spotting, etc.)
      const otherPay = Math.max(0, totalPay - lineHaul - fuelSurcharge - tolls);
      
      // Format date for storage
      let formattedDate = '';
      if (dateStr) {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const month = parts[0].padStart(2, '0');
          const day = parts[1].padStart(2, '0');
          let year = parts[2];
          if (year.length === 2) year = '20' + year;
          formattedDate = `${year}-${month}-${day}`;
        }
      }
      
      if (totalPay === 0) return null;
      
      return {
        id: crypto.randomUUID(),
        company: 'Bennett',
        date: formattedDate,
        controlNumber,
        terminal,
        origin,
        destination,
        miles,
        lineHaul,
        fuelSurcharge,
        tolls,
        otherPay,
        totalPay,
        deductions,
        checkAmount,
        selected: true
      };
    } catch (e) {
      console.error('Bennett parse error:', e);
      return null;
    }
  };

  // Main parser - detects company and routes to appropriate parser
  const parseSettlementText = (text: string): ParsedSettlement[] => {
    const settlements: ParsedSettlement[] = [];
    
    // Check for Coldiron format (Remittance Advice)
    if (text.includes('Coldiron') || (text.includes('Remittance Advice') && text.includes('Driver Card'))) {
      // Split by "Remittance Advice" or paid date patterns
      const coldiSections = text.split(/(?=Remittance Advice)/i).filter(s => s.trim());
      for (const section of coldiSections) {
        const settlement = parseColdiron(section);
        if (settlement) {
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) settlements.push(settlement);
        }
      }
    }
    
    // Check for Quality Driveaway format (Driver #)
    if (text.includes('Driver #') && (text.includes('Taxable Earnings') || text.includes('Net Settlement'))) {
      // Split by Order # patterns
      const qdSections = text.split(/(?=Order #|\d{6}\s+\d{1,2}\/\d{1,2}\/\d{4})/i).filter(s => s.trim());
      for (const section of qdSections) {
        const settlement = parseQualityDriveaway(section);
        if (settlement) {
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) settlements.push(settlement);
        }
      }
    }
    
    // Check for Auto Driveaway format
    if (text.includes('Auto Driveaway') || text.match(/\d{3}-\d{6}-\d{3}/)) {
      // Split by BOL patterns for multiple Auto Driveaway settlements
      const adSections = text.split(/(?=\d{1,2}\/\d{1,2}\/\d{4}\s+DOUG)/i);
      for (const section of adSections) {
        const settlement = parseAutoDriveawaySettlement(section);
        if (settlement) {
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) settlements.push(settlement);
        }
      }
    }
    
    // Check for Bennett format (SETTLEMENT PAY or CONTROL #)
    if (text.includes('SETTLEMENT PAY') || text.includes('CONTROL #') || text.includes('BENNETT') || text.includes('SENNETT')) {
      const bennettSections = text.split(/SETTLEMENT PAY|(?=CONTROL #:)/i).filter(s => s.trim());
      for (const section of bennettSections) {
        const settlement = parseBennettSettlement(section);
        if (settlement) {
          // Avoid duplicates
          const exists = settlements.some(s => s.controlNumber === settlement.controlNumber);
          if (!exists) {
            settlements.push(settlement);
          }
        }
      }
    }
    
    // Generic fallback - look for common settlement patterns
    if (settlements.length === 0) {
      // Try to find any settlement-like data
      const genericMatch = text.match(/(?:settlement|total pay|check amount)[:\s]*\$?([\d,]+\.?\d*)/gi);
      if (genericMatch && genericMatch.length > 0) {
        // Found potential settlement amounts, try basic parsing
        const dateMatch = text.match(/(\d{1,2}\/\d{1,2}\/\d{2,4})/);
        const amountMatch = text.match(/(?:total|settlement|pay)[:\s]*\$?([\d,]+\.?\d*)/i);
        
        if (amountMatch) {
          const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
          if (amount > 0) {
            let formattedDate = '';
            if (dateMatch) {
              const parts = dateMatch[1].split('/');
              const month = parts[0].padStart(2, '0');
              const day = parts[1].padStart(2, '0');
              let year = parts[2];
              if (year.length === 2) year = '20' + year;
              formattedDate = `${year}-${month}-${day}`;
            }
            
            settlements.push({
              id: crypto.randomUUID(),
              company: 'Other',
              date: formattedDate,
              controlNumber: `MANUAL-${Date.now()}`,
              terminal: 'N/A',
              origin: '',
              destination: '',
              miles: 0,
              lineHaul: amount,
              fuelSurcharge: 0,
              tolls: 0,
              otherPay: 0,
              totalPay: amount,
              deductions: 0,
              checkAmount: amount,
              selected: true
            });
          }
        }
      }
    }
    
    return settlements;
  };

  // Extract text from PDF
  const extractTextFromPDF = async (file: File): Promise<string> => {
    const arrayBuffer = await file.arrayBuffer();
    const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
    
    let fullText = '';
    for (let i = 1; i <= pdf.numPages; i++) {
      const page = await pdf.getPage(i);
      const textContent = await page.getTextContent();
      const pageText = textContent.items
        .map((item: any) => item.str)
        .join(' ');
      fullText += pageText + '\n\n';
    }
    
    return fullText;
  };

  // Handle file drop
  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const file = acceptedFiles[0];
    if (!file) return;
    
    setIsLoading(true);
    setError(null);
    setImportStatus(null);
    
    try {
      const text = await extractTextFromPDF(file);
      setPdfText(text);
      
      const settlements = parseSettlementText(text);
      if (settlements.length === 0) {
        setError('No settlements found in PDF. Try pasting the text manually below.');
      } else {
        setParsedSettlements(settlements);
      }
    } catch (e: any) {
      console.error('PDF extraction error:', e);
      setError(`Failed to read PDF: ${e.message}. Try pasting the text manually.`);
    } finally {
      setIsLoading(false);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1
  });

  // Parse from pasted text
  const handleParseText = () => {
    if (!pdfText.trim()) return;
    
    setError(null);
    const settlements = parseSettlementText(pdfText);
    if (settlements.length === 0) {
      setError('No settlements found in the text. Make sure you copied the full settlement content.');
    } else {
      setParsedSettlements(settlements);
    }
  };

  // Toggle settlement selection
  const toggleSelection = (id: string) => {
    setParsedSettlements(prev => 
      prev.map(s => s.id === id ? { ...s, selected: !s.selected } : s)
    );
  };

  // Remove settlement from list
  const removeSettlement = (id: string) => {
    setParsedSettlements(prev => prev.filter(s => s.id !== id));
  };

  // Get company badge color
  const getCompanyColor = (company: string) => {
    switch (company) {
      case 'Bennett': return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'Auto Driveaway': return 'bg-purple-500/20 text-purple-400 border-purple-500/50';
      case 'Coldiron': return 'bg-orange-500/20 text-orange-400 border-orange-500/50';
      case 'Quality Driveaway': return 'bg-green-500/20 text-green-400 border-green-500/50';
      default: return 'bg-gray-500/20 text-gray-400 border-gray-500/50';
    }
  };

  // Import selected settlements with duplicate protection
  const handleImport = async () => {
    const selected = parsedSettlements.filter(s => s.selected);
    if (selected.length === 0) return;
    
    setIsLoading(true);
    let success = 0;
    let errors = 0;
    let skipped = 0;
    
    for (const settlement of selected) {
      // Check for duplicates before importing
      if (isDuplicate(settlement.controlNumber)) {
        console.warn(`Skipping duplicate: ${settlement.controlNumber}`);
        skipped++;
        continue;
      }
      
      try {
        const description = settlement.company === 'Bennett'
          ? `Bennett Settlement - ${settlement.terminal} Terminal - ${settlement.origin} to ${settlement.destination}`
          : settlement.company === 'Auto Driveaway'
          ? `Auto Driveaway Settlement - BOL ${settlement.controlNumber} (${settlement.miles} miles)`
          : settlement.company === 'Coldiron'
          ? `Coldiron Settlement - Invoice ${settlement.controlNumber} - ${settlement.destination}`
          : settlement.company === 'Quality Driveaway'
          ? `Quality Driveaway Settlement - Order ${settlement.controlNumber} - ${settlement.origin} to ${settlement.destination}`
          : `Settlement - ${settlement.controlNumber}`;
        
        const filename = settlement.company === 'Bennett'
          ? `settlement-${settlement.controlNumber}-${settlement.terminal.toLowerCase()}.pdf`
          : `settlement-${settlement.company.toLowerCase().replace(/\s+/g, '-')}-${settlement.controlNumber}.pdf`;
        
        await addExpense({
          category: 'settlements',
          description,
          amount: settlement.totalPay,
          date: settlement.date,
          file: new Blob([`${settlement.company} Settlement ${settlement.controlNumber}`], { type: 'text/plain' }),
          filename,
          freightBillNumber: settlement.controlNumber
        });
        success++;
      } catch (e) {
        console.error('Failed to import settlement:', e);
        errors++;
      }
    }
    
    setImportStatus({ success, errors, skipped });
    if (success > 0 || skipped > 0) {
      // Remove successfully imported AND skipped duplicates from the list
      const importedOrSkipped = selected.map(s => s.controlNumber);
      setParsedSettlements(prev => prev.filter(s => !importedOrSkipped.includes(s.controlNumber)));
    }
    setIsLoading(false);
  };

  // Filter out duplicates from counts
  const selectedCount = parsedSettlements.filter(s => s.selected && !isDuplicate(s.controlNumber)).length;
  const totalAmount = parsedSettlements
    .filter(s => s.selected && !isDuplicate(s.controlNumber))
    .reduce((sum, s) => sum + s.totalPay, 0);
  const duplicateCount = parsedSettlements.filter(s => isDuplicate(s.controlNumber)).length;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-white">Import Settlements</h1>
        <p className="text-shuttle-sky mt-1">
          Upload settlement PDFs from Bennett, Auto Driveaway, or other carriers
        </p>
      </div>

      {/* Supported Companies */}
      <div className="flex flex-wrap gap-2">
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-blue-500/20 text-blue-400 border border-blue-500/50">
          ✓ Bennett
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-purple-500/20 text-purple-400 border border-purple-500/50">
          ✓ Auto Driveaway
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-orange-500/20 text-orange-400 border border-orange-500/50">
          ✓ Coldiron
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400 border border-green-500/50">
          ✓ Quality Driveaway
        </span>
        <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-500/20 text-gray-400 border border-gray-500/50">
          + Other Carriers
        </span>
      </div>

      {/* Upload Zone */}
      <div
        {...getRootProps()}
        className={`border-2 border-dashed rounded-2xl p-8 text-center cursor-pointer transition-all ${
          isDragActive 
            ? 'border-shuttle-sand bg-shuttle-sand/10' 
            : 'border-white/30 hover:border-white/50 bg-white/5'
        }`}
      >
        <input {...getInputProps()} />
        <FileUp className="w-12 h-12 mx-auto mb-4 text-shuttle-sky" />
        {isDragActive ? (
          <p className="text-white text-lg">Drop the PDF here...</p>
        ) : (
          <>
            <p className="text-white text-lg mb-2">
              Drag & drop your settlement PDF here
            </p>
            <p className="text-white/60 text-sm">or click to browse</p>
          </>
        )}
      </div>

      {/* Manual Text Input */}
      <div className="bg-white/10 backdrop-blur-md rounded-2xl p-6 border border-white/20">
        <h3 className="text-lg font-medium text-white mb-3 flex items-center gap-2">
          <FileText className="w-5 h-5" />
          Or Paste Settlement Text
        </h3>
        <textarea
          value={pdfText}
          onChange={(e) => setPdfText(e.target.value)}
          placeholder="Paste settlement text from any carrier - Bennett, Auto Driveaway, or others..."
          className="w-full h-32 bg-white/10 border border-white/20 rounded-xl px-4 py-3 text-white placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-shuttle-sand resize-none font-mono text-sm"
        />
        <button
          onClick={handleParseText}
          disabled={!pdfText.trim()}
          className="mt-3 px-6 py-2 bg-shuttle-sky/20 text-shuttle-sky rounded-lg hover:bg-shuttle-sky/30 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Parse Text
        </button>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-8 h-8 animate-spin text-shuttle-sand" />
          <span className="ml-3 text-white">Processing...</span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-500/20 border border-red-500/50 rounded-xl p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-red-200">{error}</p>
        </div>
      )}

      {/* Import Status */}
      {importStatus && (
        <div className={`${importStatus.success > 0 ? 'bg-green-500/20 border-green-500/50' : 'bg-yellow-500/20 border-yellow-500/50'} border rounded-xl p-4 flex items-start gap-3`}>
          <CheckCircle className={`w-5 h-5 ${importStatus.success > 0 ? 'text-green-400' : 'text-yellow-400'} flex-shrink-0 mt-0.5`} />
          <div>
            <p className={importStatus.success > 0 ? 'text-green-200' : 'text-yellow-200'}>
              {importStatus.success > 0 && `Successfully imported ${importStatus.success} settlement(s)!`}
              {importStatus.errors > 0 && ` ${importStatus.errors} failed.`}
            </p>
            {importStatus.skipped > 0 && (
              <p className="text-yellow-300 text-sm mt-1">
                ⚠️ {importStatus.skipped} duplicate(s) skipped - already in database!
              </p>
            )}
          </div>
        </div>
      )}

      {/* Parsed Settlements */}
      {parsedSettlements.length > 0 && (
        <div className="bg-white/10 backdrop-blur-md rounded-2xl border border-white/20 overflow-hidden">
          <div className="p-4 border-b border-white/10 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h3 className="text-lg font-medium text-white">
                Found {parsedSettlements.length} Settlement(s)
              </h3>
              {duplicateCount > 0 && (
                <p className="text-yellow-400 text-sm">
                  ⚠️ {duplicateCount} already in database
                </p>
              )}
            </div>
            <div className="flex items-center gap-4">
              <span className="text-shuttle-sky">
                {selectedCount} new • ${totalAmount.toLocaleString(undefined, { minimumFractionDigits: 2 })}
              </span>
              <button
                onClick={handleImport}
                disabled={selectedCount === 0 || isLoading}
                className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-shuttle-sand to-amber-500 text-shuttle-dark rounded-lg font-medium hover:from-amber-400 hover:to-amber-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
              >
                <Save className="w-4 h-4" />
                Import Selected
              </button>
            </div>
          </div>

          <div className="divide-y divide-white/10">
            {parsedSettlements.map((settlement) => {
              const isExisting = isDuplicate(settlement.controlNumber);
              return (
              <div
                key={settlement.id}
                className={`p-4 flex items-center gap-4 transition-colors ${
                  isExisting ? 'bg-yellow-500/10 opacity-60' : settlement.selected ? 'bg-white/5' : 'bg-transparent opacity-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={settlement.selected && !isExisting}
                  onChange={() => !isExisting && toggleSelection(settlement.id)}
                  disabled={isExisting}
                  className={`w-5 h-5 rounded border-white/30 bg-white/10 text-shuttle-sand focus:ring-shuttle-sand ${isExisting ? 'cursor-not-allowed opacity-50' : ''}`}
                />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-2xl">{isExisting ? '⚠️' : '💰'}</span>
                    {isExisting && (
                      <span className="px-2 py-0.5 rounded text-xs font-medium border bg-yellow-500/20 text-yellow-400 border-yellow-500/50">
                        DUPLICATE
                      </span>
                    )}
                    <span className={`px-2 py-0.5 rounded text-xs font-medium border ${getCompanyColor(settlement.company)}`}>
                      <Building2 className="w-3 h-3 inline mr-1" />
                      {settlement.company}
                    </span>
                    {settlement.terminal && settlement.terminal !== 'N/A' && (
                      <>
                        <span className="text-white/50">•</span>
                        <span className="font-medium text-white">
                          {settlement.terminal} Terminal
                        </span>
                      </>
                    )}
                    <span className="text-white/50">•</span>
                    <span className="text-white/70">
                      #{settlement.controlNumber}
                    </span>
                    <span className="text-white/50">•</span>
                    <span className="text-white/70">
                      {settlement.date}
                    </span>
                  </div>
                  {(settlement.origin || settlement.destination) && (
                    <p className="text-white/60 text-sm truncate">
                      {settlement.origin} → {settlement.destination} • {settlement.miles.toLocaleString()} miles
                    </p>
                  )}
                  {settlement.miles > 0 && !settlement.origin && (
                    <p className="text-white/60 text-sm">
                      {settlement.miles.toLocaleString()} miles
                    </p>
                  )}
                  <div className="flex gap-4 mt-1 text-xs text-white/50 flex-wrap">
                    {settlement.lineHaul > 0 && <span>Line Haul: ${settlement.lineHaul.toFixed(2)}</span>}
                    {settlement.fuelSurcharge > 0 && <span>Fuel: ${settlement.fuelSurcharge.toFixed(2)}</span>}
                    {settlement.tolls > 0 && <span>Tolls: ${settlement.tolls.toFixed(2)}</span>}
                    {settlement.otherPay > 0 && <span>Other: ${settlement.otherPay.toFixed(2)}</span>}
                  </div>
                </div>

                <div className="text-right">
                  <p className="text-xl font-bold text-green-400">
                    ${settlement.totalPay.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </p>
                  <p className="text-xs text-white/50">
                    Check: ${settlement.checkAmount.toFixed(2)}
                  </p>
                </div>

                <button
                  onClick={() => removeSettlement(settlement.id)}
                  className="p-2 text-white/40 hover:text-red-400 transition-colors"
                >
                  <Trash2 className="w-5 h-5" />
                </button>
              </div>
            );
            })}
          </div>
        </div>
      )}

      {/* Instructions */}
      <div className="bg-white/5 rounded-2xl p-6 border border-white/10">
        <h3 className="text-lg font-medium text-white mb-3">📋 Supported Settlement Formats</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="bg-blue-500/10 rounded-lg p-4 border border-blue-500/30">
            <h4 className="font-medium text-blue-400 mb-2">🚛 Bennett</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• SETTLEMENT PAY format</li>
              <li>• Control # and Terminal codes</li>
              <li>• Line Haul, Fuel Surcharge, Tolls</li>
            </ul>
          </div>
          <div className="bg-purple-500/10 rounded-lg p-4 border border-purple-500/30">
            <h4 className="font-medium text-purple-400 mb-2">🚗 Auto Driveaway</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• BOL format (###-######-###)</li>
              <li>• Per mile breakdown</li>
              <li>• Advance and Settlement totals</li>
            </ul>
          </div>
          <div className="bg-orange-500/10 rounded-lg p-4 border border-orange-500/30">
            <h4 className="font-medium text-orange-400 mb-2">🚚 Coldiron</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Remittance Advice format</li>
              <li>• Invoice # and Driver Card</li>
              <li>• Driver Pay, Fuel, Deadhead</li>
            </ul>
          </div>
          <div className="bg-green-500/10 rounded-lg p-4 border border-green-500/30">
            <h4 className="font-medium text-green-400 mb-2">🚐 Quality Driveaway</h4>
            <ul className="text-sm text-white/70 space-y-1">
              <li>• Driver # and Order # format</li>
              <li>• Mileage Pay / Flat Rate</li>
              <li>• Taxable Earnings breakdown</li>
            </ul>
          </div>
        </div>
        <p className="mt-4 text-white/50 text-xs">
          💡 Just paste or upload any settlement - the parser auto-detects the format!
        </p>
      </div>
    </div>
  );
}
